import { useEffect, useRef, useState } from "react";
import { useAction } from "convex/react";
import { Loader2 } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { PremiumStatus } from "../OnboardingFlow";
import { PaywallScreen } from "./PaywallScreen";

interface PaywallGateProps {
  readonly completing: boolean;
  readonly onChoose: (status: PremiumStatus, choice: string) => void;
}

/**
 * Gates the paywall behind a server-side AI credit check. Only a confirmed
 * insufficient balance shows the paywall; sufficient credits or a failed
 * check complete onboarding directly with the free tier.
 */
export function PaywallGate({ completing, onChoose }: PaywallGateProps) {
  const checkAiCredits = useAction(api.ai.checkAiCredits);
  const startedRef = useRef(false);
  const settledRef = useRef(false);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const settle = (status: PremiumStatus, source: string) => {
      if (settledRef.current) return;
      settledRef.current = true;
      onChoose(status, source);
    };

    console.log("[Frontend][OnboardingFlow] AI credit check started");
    checkAiCredits({})
      .then((result) => {
        console.log("[Frontend][OnboardingFlow] AI credit check result", {
          sufficient: result.sufficient,
          reason: result.reason,
        });
        if (result.sufficient) {
          settle("free", "credits_sufficient");
        } else {
          console.log("[Frontend][OnboardingFlow] Insufficient AI credits, showing paywall", {
            reason: result.reason,
          });
          setShowPaywall(true);
        }
      })
      .catch((error: unknown) => {
        console.warn("[Frontend][OnboardingFlow] AI credit check failed, treating as sufficient", {
          error: error instanceof Error ? error.message : String(error),
        });
        settle("free", "credit_check_error");
      });
  }, [checkAiCredits, onChoose]);

  if (showPaywall) {
    return <PaywallScreen completing={completing} onChoose={onChoose} />;
  }

  return (
    <div
      data-testid="paywall-gate"
      className="flex flex-1 flex-col items-center justify-center gap-3"
    >
      <Loader2 size={28} className="animate-spin text-accent" aria-hidden="true" />
      <p role="status" className="text-sm text-text-muted">
        Checking your AI credits…
      </p>
    </div>
  );
}
